import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  ListItem,
  Stack,
  Typography,
} from '@mui/material';
import { Formik, FormikHelpers } from 'formik';
import { useEffect, useState } from 'react';
import { PoliciesService } from 'services';
import { ManageTeamService } from 'services/ManageTeamService';
import {
  AppFormikSelectWithKeys,
  AppFormikTextField,
  AppPrimaryButton,
} from 'shared/components/AppComponents';
import { Utils } from 'shared/helpers';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState, EMP_ROLES } from 'state/authentication/Reducer';
import { PolicyApprovalSchema } from 'validators/PolicyManagementScheme';
import { IAppState } from 'state/RootReducer';
import { IInvitation } from 'modules/settings/management/manage-team/components/types';
import { PaginationActions } from 'state/pagination/Action';

interface IApprovalProps {
  policyId: string;
}
interface IApprover {
  _id: string;
  organizationId: string;
  policyId: string;
  approverId: string;
  reminders: string;
  approverRole: string;
  status: string;
  assignedDate: string;
  dueDate: string;
  approvedAt: any;
  assignedBy: string;
  isActive: boolean;
  __v: number;
  createdDate: string;
  updatedDate: string;
  assignedbyName: string;
  approverName: string;
}
const Approvals: React.FC<IApprovalProps> = (props) => {
  const { policyId } = props;
  const dispatch = useDispatch();
  const teamsPagination = useSelector(
    (state: IAppState) => state.paginationState.teams
  );
  const [teamMembers, setTeamMembers] = useState<IInvitation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [approvers, setApprovers] = useState<IApprover[]>([]);
  const { role, userId }: AuthState = useSelector(
    (state: any) => state.authState
  );

  // API Calls
  const loadApprovers = (): void => {
    setIsLoading(true);
    PoliciesService.approvers(policyId)
      .then((response) => {
        console.log(response);
        const { data } = response.data;
        setApprovers(data);
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };
  const addApprovers = (payload: any, callback: any): void => {
    PoliciesService.addApprover(payload)
      .then((response) => {
        callback(response);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
        callback(false);
      });
  };
  const loadTeamMembers = (): void => {
    const queryString = new URLSearchParams({
      offset: String(teamsPagination.offset),
      limit: String(teamsPagination.pageLimit),
      search: teamsPagination.search,
    }).toString();
    ManageTeamService.teamMembers(queryString).then((response) => {
      const { user_list, pagination } = response.data.data;
      if (user_list.length > 0) {
        setTeamMembers(user_list);

        dispatch(
          PaginationActions.setPagination({
            teams: {
              offset: pagination.offset,
              pageLimit: pagination.limit,
              totalRecords: pagination.total,
              totalPages: pagination.totalPages,
            },
          })
        );
      }
    });
  };
  const removeApprover = (id: string, callback: any): void => {
    PoliciesService.deleteApprover(id)
      .then(() => {
        callback(true);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
        callback(false);
      });
  };
  const approvePolicy = (id: string, callback: any): void => {
    PoliciesService.approvePolicy(id)
      .then(() => {
        callback(true);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
        callback(false);
      });
  };

  // UI Actions
  const actionOnSubmit = (
    formValues: any,
    formikHelper: FormikHelpers<any>
  ): void => {
    console.log(formValues, formikHelper);
    addApprovers(
      { ...formValues, policyId, dueDate: '2027-01-13T07:31:28.152+00:00' },
      (response: any) => {
        formikHelper.setSubmitting(false);
        if (typeof response === 'object') {
          loadApprovers();
          formikHelper.resetForm();
        }
      }
    );
  };
  const actionOnRemoveApprover = (approver: IApprover): void => {
    removeApprover(approver._id, (status: boolean) => {
      if (status) {
        loadApprovers();
      }
    });
  };
  const actionApprovePolicy = (approver: IApprover): void => {
    approvePolicy(approver._id, (status: boolean) => {
      if (status) {
        loadApprovers();
      }
    });
  };

  // Hooks
  useEffect(() => {
    loadTeamMembers();
    loadApprovers();
  }, []);

  // UI Elements
  return (
    <>
      {role === EMP_ROLES.Admin && (
        <Box className="app-plain-card policy-approvals">
          <Box className="title">
            <Typography variant="h4">Add Approver</Typography>
          </Box>
          <Box className="content">
            <Grid spacing={5} container alignItems="flex-start">
              <Formik
                initialValues={{ approverRole: '', approverId: '' }}
                onSubmit={(formValues, formikHelper) => {
                  actionOnSubmit(formValues, formikHelper);
                }}
                validateOnMount
                validationSchema={PolicyApprovalSchema}
              >
                {({ isSubmitting, handleSubmit }) => (
                  <>
                    <Grid item xs={4}>
                      <AppFormikSelectWithKeys
                        name="approverId"
                        label="Approver"
                        placeholder="Choose Approver"
                        titleKey="name"
                        valueKey="userId"
                        items={teamMembers}
                        outerLabel
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <AppFormikTextField
                        name="approverRole"
                        placeholder="e.g, Leagal Team, Security Officer"
                        label="Role"
                        outerLabel
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <AppPrimaryButton
                        className="mt-7"
                        title="Add"
                        onClick={handleSubmit}
                        isLoading={isSubmitting}
                      />
                    </Grid>
                  </>
                )}
              </Formik>
            </Grid>
          </Box>
        </Box>
      )}

      <Box className="app-plain-card policy-approvals">
        <Box className="title">
          <Typography variant="h4">Approvers</Typography>
        </Box>
        <Box className="content">
          {approvers.map((approver) => (
            <ListItem className="app-box-border mt-4">
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ width: '100%' }}
              >
                <Stack>
                  <Stack
                    className="text-primary mb-2"
                    alignItems="center"
                    direction="row"
                    spacing={2}
                  >
                    <Typography className="f18 fw-6">
                      {approver.approverName}
                    </Typography>
                    <Chip
                      size="small"
                      label={
                        approver.status === 'assigned' ? 'Pending' : 'Approved'
                      }
                    />
                  </Stack>
                  <Typography variant="body1" className="text-gray  mb-2">
                    {approver.approverRole}
                  </Typography>
                  <Typography variant="body1" className="text-gray  mb-2">
                    Requested By: {approver.assignedbyName} on{' '}
                    {Utils.formatDate(approver.createdDate)}
                  </Typography>
                  {approver.status === 'Approved' && (
                    <Typography variant="body1" className="text-gray  mb-2">
                      Approved On:
                      {Utils.formatDateTime(approver.approvedAt)}
                    </Typography>
                  )}
                </Stack>
                <Stack alignItems="center" direction="row" spacing={2}>
                  {approver.status === 'assigned' &&
                    userId === approver.approverId && (
                      <AppPrimaryButton
                        title="Approve"
                        className="btn-small"
                        onClick={() => {
                          actionApprovePolicy(approver);
                        }}
                      />
                    )}
                  {role === EMP_ROLES.Admin && approver.status === 'assigned' && (
                    <IconButton
                      onClick={() => {
                        actionOnRemoveApprover(approver);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Stack>
              </Stack>
            </ListItem>
          ))}

          {isLoading && (
            <Box className="empty-content">
              <CircularProgress />
            </Box>
          )}
          {!isLoading && approvers.length === 0 && (
            <Box className="empty-content">
              <Typography>No Approvers Yet</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default Approvals;
